import { useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, CircularProgress, Link, Stack, Typography, useTheme } from '@mui/material'
import { LockOutlined, LoginRounded } from '@mui/icons-material'

import { useLogin } from '../hooks/useLogin'
import usePersist from '../hooks/usePersist'
import useDialogStyles from '../hooks/useDialogStyles'
import { usePathContext } from '../context/path'
import AuthCard, { AuthError, AuthField, OrDivider, PasswordField } from '../components/auth/AuthCard'
import PersistLoginAlert from '../components/auth/PersistLoginAlert'
import PersistLoginCheckbox from '../components/auth/PersistLoginCheckbox'
import SignInWithGoogleButton from '../components/auth/SignInWithGoogleButton'

const Login = () => {
  const theme = useTheme()
  const { goldButtonSx } = useDialogStyles()
  const accent = theme.palette.secondary.main
  const { setTitle } = usePathContext()

  const { login, error, isLoading } = useLogin()
  const { persist, setPersist } = usePersist()
  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    await login(emailRef.current.value.trim(), passwordRef.current.value.trim(), persist)
  }

  useEffect(() => {
    setTitle("Welcome")
  }, [setTitle])

  const linkSx = { color: accent, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

  return (
    <AuthCard icon={LockOutlined} title="Log in" subtitle="Welcome back. Sign in to continue.">
      <Box component="form" onSubmit={handleSubmit} noValidate={false}>
        <Stack spacing={2}>
          <AuthField label="Email address" type="email" inputRef={emailRef} autoComplete="email" autoFocus required />
          <PasswordField label="Password" id="password" inputRef={passwordRef} autoComplete="current-password" required />
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          <PersistLoginCheckbox persist={persist} setPersist={setPersist} />
          <Link component={RouterLink} to="/recover-password" sx={{ ...linkSx, fontSize: '0.83rem' }}>
            Forgot password?
          </Link>
        </Box>

        {persist && <PersistLoginAlert sx={{ mt: 1.5 }} />}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <LoginRounded />}
          sx={{ ...goldButtonSx, mt: 2.5, height: 44, fontSize: '0.95rem', textTransform: 'none' }}
        >
          {isLoading ? 'Logging in…' : 'Log in'}
        </Button>

        {error && <AuthError>{error}</AuthError>}
      </Box>

      <OrDivider />
      <SignInWithGoogleButton persist={persist} setPersist={setPersist} />

      <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.85rem', color: 'text.secondary' }}>
        Don't have an account?{' '}
        <Link component={RouterLink} to="/signup" sx={linkSx}>Sign up</Link>
      </Typography>
    </AuthCard>
  )
}

export default Login
